import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  api,
  type ApiError,
  type GetCampaignResult,
  type PreviewCampaignAudienceResult,
} from '@whinerp/client';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  MetricTile,
  Num,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { formatCount, formatDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { CampaignAttributionPanel } from './attribution-panel.js';
import { AbTestPanel } from './ab-test-panel.js';
import { DirectMailPanel } from './direct-mail-panel.js';
import { CampaignBudgetPanel } from './campaign-budget-panel.js';
import { CampaignSmsTemplate } from './campaign-sms-template.js';

/**
 * SC-17 Campaign record.
 *
 * The screen exists to answer two questions, and the second is the one most
 * campaign screens do not.
 *
 * **How did it do?** Metric tiles from count(), with the parts adding up to
 * the audience.
 *
 * **Who will not get it, and why?** Shown BEFORE the send button, broken down
 * by reason, because "1,100 of 1,400" is a support ticket and "200 never opted
 * in, 80 unsubscribed, 20 bounced" is three different things to do next. The
 * figures come from the same SQL function the send gate calls, so this cannot
 * promise a number the send then misses.
 */

type Campaign = GetCampaignResult;
type Audience = PreviewCampaignAudienceResult;

/** The server's own words for a refusal, in the workspace's language. */
const REFUSAL_KEY: Record<string, 'campaign.refusal.suppressed'> = {
  suppressed: 'campaign.refusal.suppressed',
  'opted out': 'campaign.refusal.optedOut' as 'campaign.refusal.suppressed',
  'no consent recorded': 'campaign.refusal.noConsent' as 'campaign.refusal.suppressed',
  'no address': 'campaign.refusal.noAddress' as 'campaign.refusal.suppressed',
  // Asked, not yet confirmed by email (F-082). Not an opt-out.
  'awaiting confirmation': 'campaign.refusal.awaitingConfirmation' as 'campaign.refusal.suppressed',
  erased: 'campaign.refusal.erased' as 'campaign.refusal.suppressed',
};

function CampaignRecordScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const params = useParams();
  const campaignId = params['id'] ?? '';
  const [confirming, setConfirming] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const record = useQuery<Campaign, ApiError>({
    queryKey: ['campaign', campaignId],
    queryFn: () => api.getCampaign({ path: { campaignId } }),
  });

  // Loads independently of the record: the audience preview counts every
  // address against the gate and is the slower of the two, and a header that
  // waited for it would make the whole screen feel broken.
  const audience = useQuery<Audience, ApiError>({
    queryKey: ['campaign-audience', campaignId],
    queryFn: () => api.previewCampaignAudience({ path: { campaignId } }),
  });

  const send = useMutation({
    mutationFn: () => api.sendCampaign({ path: { campaignId } }),
    onSuccess: () => {
      setConfirming(false);
      setFailure(null);
      void queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
      void queryClient.invalidateQueries({ queryKey: ['campaign-audience', campaignId] });
      void queryClient.invalidateQueries({ queryKey: ['ab-test', campaignId] });
    },
    onError: (error: unknown) =>
      setFailure(error instanceof Error ? error.message : t('error.unexpected')),
  });

  if (record.isPending) return <CampaignLoading />;
  if (record.isError) return <CampaignError error={record.error} retry={() => void record.refetch()} />;

  const { campaign, performance } = record.data;
  const blocked = !campaign.segmentId
    ? t('campaign.needsAudience')
    : !campaign.documentId
      ? t('campaign.needsContent')
      : undefined;

  return (
    <div data-component="CampaignRecord" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-end gap-4">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h1 className="truncate text-h1 text-ink">{campaign.name}</h1>
          <p className="text-meta text-ink-meta">
            {campaign.channel} · {campaign.status}
            {campaign.sentAt ? ` · ${formatDate(campaign.sentAt, format)}` : ''}
          </p>
        </div>

        <span className="ms-auto flex flex-wrap items-start gap-6">
          {(
            [
              ['members', performance.members],
              ['sent', performance.sent],
              ['refused', performance.refused],
              ['opened', performance.opened],
              ['clicked', performance.clicked],
            ] as const
          ).map(([key, value]) => (
            <MetricTile
              key={key}
              data-metric={key}
              label={t(`campaign.${key}` as 'campaign.members')}
              value={formatCount(value, format)}
              labels={{ up: (c) => c, down: (c) => c, flat: '' }}
            />
          ))}
        </span>
      </header>

      {failure ? (
        <p role="alert" data-send-failed className="rounded-card bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {failure}
        </p>
      ) : null}

      {/*
       * Who will not receive it, before the send button rather than after.
       *
       * This is the part a campaign screen usually leaves out, and leaving it
       * out is how somebody sends to an audience of 1,400 and spends the
       * afternoon working out why 1,100 arrived.
       */}
      {/* Planned against actual spend (G-165). */}
      <CampaignBudgetPanel campaignId={campaignId} />
      {/* What this campaign's tracking code was credited with (F-080). */}
      <CampaignAttributionPanel campaignId={campaignId} />
      {/* The A/B test, before the send it changes (F-066). */}
      <AbTestPanel campaignId={campaignId} campaignStatus={campaign.status} documentId={campaign.documentId} />
      {campaign.channel === 'sms' ? <CampaignSmsTemplate campaignId={campaignId} editable={campaign.status === 'draft'} /> : null}
      {/* Post goes out as a file for a mailing house (G-161). */}
      {campaign.channel === 'post' ? <DirectMailPanel campaignId={campaignId} campaignName={campaign.name} /> : null}

      <Panel>
        <SectionLabel>{t('campaign.audience')}</SectionLabel>
        {audience.isPending ? (
          <Skeleton className="h-plot w-full" />
        ) : audience.isError ? (
          <p role="alert" data-audience-failed className="text-dense text-danger-text">
            {audience.error.message}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <Num data-reach-preview className="text-lead text-ink">
              {t('campaign.reachPreview', {
                values: {
                  reachable: formatCount(audience.data.reachable, format),
                  total: formatCount(audience.data.total, format),
                },
              })}
            </Num>

            {audience.data.refusals.length === 0 ? null : (
              <div className="flex flex-col gap-1">
                <SectionLabel>{t('campaign.whyNot')}</SectionLabel>
                <ul data-refusals className="flex flex-col gap-0.5">
                  {audience.data.refusals.map((refusal) => (
                    <li
                      key={refusal.reason}
                      data-refusal={refusal.reason}
                      className="flex items-baseline justify-between gap-3 border-t border-t-rule py-1 text-dense text-ink"
                    >
                      {/* The workspace's own words, not the engine's. Three
                          reasons, three different things to do next. */}
                      <span>{t(REFUSAL_KEY[refusal.reason] ?? 'campaign.refusal.noConsent')}</span>
                      <Num data-refusal-count={refusal.reason} className="text-dense text-ink-meta">
                        {t('campaign.refusalCount', {
                          values: { count: formatCount(refusal.count, format) },
                        })}
                      </Num>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <span>
              <Button
                intent="primary"
                icon="PaperPlaneTilt"
                data-send-campaign
                busy={send.isPending}
                disabled={campaign.status === 'sent'}
                onClick={() => setConfirming(true)}
              >
                {t('campaign.send')}
              </Button>
              {blocked ? (
                <span data-send-blocked className="ps-2 text-meta text-warning-text">
                  {blocked}
                </span>
              ) : null}
            </span>
          </div>
        )}
      </Panel>

      <Panel>
        <SectionLabel>{t('campaign.tab.content')}</SectionLabel>
        {campaign.documentId ? (
          <a
            href={`/emails/${campaign.documentId}/edit`}
            data-open-content
            className="text-dense text-bronze-text underline underline-offset-4"
          >
            {campaign.documentName ?? t('campaign.content')}
          </a>
        ) : (
          <p className="text-dense text-ink-meta">{t('campaign.needsContent')}</p>
        )}
      </Panel>

      <ConfirmDialog
        open={confirming}
        data-send-dialog
        title={t('campaign.confirmSend', {
          values: { count: formatCount(audience.data?.reachable ?? 0, format) },
        })}
        confirmLabel={t('campaign.send')}
        confirmIntent="danger"
        busy={send.isPending}
        /* The named blocker rather than a disabled button in silence: a person
           should learn what is missing before pressing, not after. */
        blockedReason={blocked}
        labels={{ cancel: t('action.cancel') }}
        onCancel={() => setConfirming(false)}
        onConfirm={() => send.mutate()}
      >
        <p className="text-body text-ink">{t('campaign.confirmSendBody')}</p>
      </ConfirmDialog>
    </div>
  );
}

function CampaignEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Megaphone"
      title={t('campaign.empty.title')}
      body={t('campaign.empty.body')}
      primary={{ label: t('empty.action.newCampaign'), icon: 'Plus', href: '/campaigns' }}
    />
  );
}

function CampaignLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      {/* Metric tiles first: they are what tells somebody they opened the
          right campaign. */}
      <div className="flex gap-6">
        {Array.from({ length: 5 }, (_, tile) => (
          <Skeleton key={tile} className="h-line-h1 w-panel-narrow" />
        ))}
      </div>
      <Skeleton className="h-plot w-full" />
    </div>
  );
}

function CampaignRecordError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This campaign could not be loaded"
      body={error instanceof Error ? error.message : 'The campaign did not load.'}
      onRetry={retry}
    />
  );
}

const CampaignError = CampaignRecordError;

export const screen = defineScreen({
  id: 'SC-17',
  route: '/campaigns/:id',
  surface: 'page',
  title: 'Campaign',
  features: ['F-062', 'F-081', 'F-080'],
  entitlement: 'F-062',
  reachedBy: [{ kind: 'screen', from: 'SC-16', how: 'a row in the campaign list' }],
  components: ['MetricTile'],
  elements: ['CampaignRecord'],
  // The dialog is not on the page until somebody asks to send, which is the
  // point of it - a send is irreversible and gets a confirmation naming the
  // number. Declared here so a UI test has to drive the screen until it
  // appears rather than the render check taking it on trust.
  afterInteraction: ['ConfirmDialog'],
  sampleRecord: { objectKey: 'campaign', param: 'id' },
  states: { empty: CampaignEmpty, loading: CampaignLoading, error: CampaignRecordError },
  component: CampaignRecordScreen,
});

export default screen;
